<template>
  <div class="w-100">
    <div
      v-if="pageLoading"
      class="form-wrapper"
    >
      <v-skeleton-loader
        type="text"
        width="220"
        height="24"
        class="mb-4"
      />
      <v-skeleton-loader
        type="text"
        width="100%"
        height="48"
        class="mb-4"
      />
      <div class="d-flex align-center ga-2 mb-4">
        <v-skeleton-loader
          type="text"
          width="75"
          height="40"
        />
        <v-skeleton-loader
          type="text"
          width="36"
          height="20"
        />
      </div>
      <v-skeleton-loader
        type="button"
        width="120"
        height="40"
      />
    </div>

    <div
      v-else
      class="form-wrapper"
    >
      <v-form class="approvals-form">
        <div>
          <h2 class="text-h1 text-textDarkest mb-0 w-100">
            Approvals
          </h2>
          <p class="section-description mb-0">
            Configure default templates and approver permissions for your organization's approval workflows.
          </p>
        </div>

        <div class="field-block">
          <h3 class="text-h2 text-textDarkest mb-0">
            Default Template
          </h3>
          <p class="section-desc">
            The template applied by default when creating a new approval flow.
            You can override this on a per-flow basis.
          </p>
          <ApprovalTemplateDropdown
            v-model="selectedTemplateUlid"
            class="mt-2 template-dropdown"
            :items="activeTemplates"
            :selected-template="selectedTemplate"
            @update:model-value="updateSelectedTemplate"
          />
        </div>

        <div class="w-100">
          <DpplNewButton
            color="primary"
            variant="flat"
            :disabled="selectedTemplateIsCurrentTemplate"
            :loading="componentLoading"
            type="submit"
            @click="handleSaveChanges"
          >
            Save Changes
          </DpplNewButton>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useApprovalFlows } from '@/stores/jobProfiles/approvalFlows';
import { useApprovalsSettings } from '@/stores/settings/approvalsSettings';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import ApprovalTemplateDropdown from '@/components/jobProfiles/editor/approvals/ApprovalTemplateDropdown.vue';

// Stores
const approvalFlowsStore = useApprovalFlows();
const {
  activeTemplates,
} = storeToRefs(approvalFlowsStore);

const appLoadingStore = useAppLoading();
const { componentLoading, pageLoading } = storeToRefs(appLoadingStore);

const { load, updateDefaultApprovalTemplate } = useApprovalsSettings();
const { defaultTemplateUlid } = storeToRefs(useApprovalsSettings());

// State
const selectedTemplateUlid = ref(null);
const selectedTemplate = ref(null);

// Computed
const selectedTemplateIsCurrentTemplate = computed(
  () => selectedTemplateUlid.value === defaultTemplateUlid.value,
);

// Methods
const handleSaveChanges = () => {
  if (!selectedTemplateUlid.value) return;

  updateDefaultApprovalTemplate(selectedTemplateUlid.value);
};

const updateSelectedTemplate = () => {
  const templateItem = activeTemplates.value.find(
    (item) => item.ulid === selectedTemplateUlid.value,
  );

  selectedTemplate.value = templateItem;
};

// Lifecycle hooks
onMounted(async () => {
  await load();

  selectedTemplateUlid.value = defaultTemplateUlid.value;
  updateSelectedTemplate();
});
</script>

<style lang="less" scoped>
/* Copied from FlsaSettings.vue */
.form-wrapper {
  border-radius: 10px;
  background: @white;
  padding: 40px;
}

.approvals-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
}

.section-desc {
  font-size: 16px;
  font-weight: 500;
  line-height: 150%;
  color: @text-dark;
}

.section-description {
  max-width: 100%;
  font-size: 16px;
  font-weight: 500 !important;
  line-height: 1.45;
  color: @text-dark;
}

.field-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

/* For this component */
.template-dropdown {
  width: fit-content;
  min-width: 250px;
}
</style>
